import type { FC } from "react";
import {
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import type { ChipProps } from "@mui/material/Chip";
import { CustomCellDate, ErrorMessage } from "@/shared/components";
import { useRefundsQuery } from "@/admin/entities/refunds";

interface PaymentRefundsProps {
  paymentId: string;
}

// Тот же локальный маппинг статусов, что и в feature refunds — admin
// не переиспользует partner-локализацию, здесь нет i18n.
const statusChipConfig: Record<
  string,
  { label: string; color: ChipProps["color"] }
> = {
  PENDING: { label: "В обработке", color: "warning" },
  SUCCEEDED: { label: "Выполнен", color: "success" },
  FAILED: { label: "Ошибка", color: "error" },
};

// REFUND_INITIATORS бэкенда: возврат оформлен админом или пришёл вебхуком
// провайдера (возврат сделан в его дашборде).
const initiatedByLabel: Record<string, string> = {
  ADMIN: "Администратор",
  WEBHOOK: "Провайдер",
};

export const PaymentRefunds: FC<PaymentRefundsProps> = ({ paymentId }) => {
  const { data, loading, error } = useRefundsQuery({
    variables: { filter: { paymentId } },
    fetchPolicy: "cache-and-network",
  });

  const nodes = data?.refunds?.nodes ?? [];

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
          Возвраты
        </Typography>

        {error && <ErrorMessage error={error} />}
        {loading && !data && <LinearProgress />}

        {!loading && !error && nodes.length === 0 && (
          <Typography variant="body2" color="textSecondary">
            Возвратов по этому платежу нет
          </Typography>
        )}

        {nodes.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Дата</TableCell>
                <TableCell>Сумма</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Инициатор</TableCell>
                <TableCell>Комментарий</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {nodes.map((refund) => {
                const { label, color } = statusChipConfig[refund.status] ?? {
                  label: refund.status,
                  color: "default" as const,
                };
                return (
                  <TableRow key={refund.id}>
                    <TableCell>
                      <CustomCellDate date={refund.createdAt as string} />
                    </TableCell>
                    <TableCell>
                      {refund.amount.value} {refund.amount.currency}
                    </TableCell>
                    <TableCell>
                      <Chip label={label} color={color} size="small" />
                    </TableCell>
                    <TableCell>
                      {initiatedByLabel[refund.initiatedBy] ??
                        refund.initiatedBy}
                    </TableCell>
                    <TableCell>{refund.description ?? ""}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};
